import React from "react";
import { RotateCcw } from "lucide-react";
import {
  fetchRemoteConfig,
  setRemoteFlag,
  resetRemoteFlag,
  type RemoteFlag,
  type RemoteConfigResponse
} from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * Remote config / feature flags. Per-environment, non-secret. Each environment's
 * values drive that environment's apps (dev admin -> dev flags -> dev apps).
 * Secrets are never shown here — they stay in env / Secrets Manager.
 */
export function RemoteConfigPage() {
  usePageTitle("Remote config");
  const [data, setData] = React.useState<RemoteConfigResponse | null>(null);
  const [environment, setEnvironment] = React.useState<string>("");
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(true);
  const [savingKey, setSavingKey] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const load = React.useCallback((env?: string) => {
    setLoading(true);
    setError(null);
    fetchRemoteConfig(env)
      .then((res) => {
        setData(res);
        setEnvironment(res.environment);
        setDrafts({});
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load remote config"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const draftValue = (f: RemoteFlag) => drafts[f.key] ?? f.value;
  const isDirty = (f: RemoteFlag) => draftValue(f) !== f.value;
  const setDraft = (key: string, value: string) => setDrafts((d) => ({ ...d, [key]: value }));

  const save = async (f: RemoteFlag) => {
    setSavingKey(f.key); setError(null); setNotice(null);
    try {
      await setRemoteFlag(f.key, draftValue(f), environment);
      setNotice(`Saved ${f.key} for ${environment}.`);
      load(environment);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not save ${f.key}`);
    } finally {
      setSavingKey(null);
    }
  };

  const reset = async (f: RemoteFlag) => {
    setSavingKey(f.key); setError(null); setNotice(null);
    try {
      await resetRemoteFlag(f.key, environment);
      setNotice(`Reset ${f.key} to default for ${environment}.`);
      load(environment);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not reset ${f.key}`);
    } finally {
      setSavingKey(null);
    }
  };

  const categories = React.useMemo(() => {
    const map = new Map<string, RemoteFlag[]>();
    for (const f of data?.items ?? []) {
      const arr = map.get(f.category) ?? [];
      arr.push(f);
      map.set(f.category, arr);
    }
    return Array.from(map.entries());
  }, [data]);

  return (
    <div className="page">
      <header className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <span className="eyebrow">Operations</span>
          <h1>Remote config</h1>
          <p className="muted">
            Feature flags &amp; non-secret config, per environment. Changes apply without a redeploy.
            Secrets are never stored here.
          </p>
        </div>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <label className="muted" htmlFor="rc-env">Environment</label>
          <select id="rc-env" value={environment} onChange={(e) => load(e.target.value)}>
            {(data?.environments ?? ["dev", "prod"]).map((env) => (
              <option key={env} value={env}>{env}</option>
            ))}
          </select>
        </div>
      </header>

      {error ? <div style={{ color: "var(--danger, #c0392b)", marginBottom: 12 }}>{error}</div> : null}
      {notice ? <div style={{ color: "var(--success, #2e7d32)", marginBottom: 12 }}>{notice}</div> : null}
      {loading ? <p className="muted">Loading…</p> : null}

      {!loading && categories.map(([category, flags]) => (
        <div className="card" key={category} style={{ marginBottom: 16 }}>
          <div className="section-head"><h2>{category}</h2></div>
          <div className="col" style={{ gap: 16 }}>
            {flags.map((f) => (
              <div className="row between" key={f.key} style={{ alignItems: "flex-start", gap: 16 }}>
                <div style={{ maxWidth: 460 }}>
                  <div style={{ fontWeight: 600, fontFamily: "monospace" }}>
                    {f.key}{" "}
                    {f.isOverridden
                      ? <span className="pill warn">overridden</span>
                      : <span className="pill">default</span>}
                  </div>
                  <div className="muted" style={{ marginTop: 2 }}>{f.description}</div>
                  <div className="faint" style={{ marginTop: 2 }}>
                    default: <code>{f.default}</code>
                    {f.isOverridden && f.updatedBy ? ` • set by ${f.updatedBy}` : ""}
                  </div>
                </div>
                <div className="row" style={{ gap: 8, alignItems: "center" }}>
                  <FlagControl flag={f} value={draftValue(f)} onChange={(v) => setDraft(f.key, v)} />
                  <button className="btn btn-sm" disabled={!isDirty(f) || savingKey === f.key} onClick={() => save(f)}>
                    {savingKey === f.key ? "…" : "Save"}
                  </button>
                  {f.isOverridden ? (
                    <button
                      className="btn btn-ghost btn-sm"
                      title="Reset to default"
                      disabled={savingKey === f.key}
                      onClick={() => reset(f)}
                    >
                      <RotateCcw size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FlagControl({
  flag, value, onChange
}: {
  flag: RemoteFlag;
  value: string;
  onChange: (v: string) => void;
}) {
  if (flag.type === "boolean") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (flag.type === "enum") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {(flag.options ?? []).map((o) => (
          <option key={o} value={o}>
            {flag.optionLabels?.[o] ?? o}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={flag.type === "number" ? "number" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ minWidth: 180 }}
    />
  );
}
